/**
 * Plugin registry — id-keyed maps of effects + instruments.
 *
 * Host code asks for a plugin by id, drops it into whatever slot
 * wants it (bus chain, pad voice, channel strip). Effects and
 * instruments are separate registries because hosts usually care
 * about one category at a time (an FX rack vs an instrument
 * picker).
 *
 * Registration is idempotent — calling `registerEffect` twice with
 * the same id overwrites the previous registration. This lets us
 * hot-reload during development without accumulating ghost plugins.
 *
 * The existing hardcoded effect factories in `audio/effects.ts`
 * and pad kinds in `useAudioEngine` stay authoritative for now;
 * the registry runs alongside them and gets populated with the
 * same kinds via `builtins.ts` so future refactors can swap the
 * hardcoded switches for registry lookups without a data
 * migration (both paths use the same id strings).
 */

import type { EffectPlugin, InstrumentPlugin } from './types'

const effectRegistry = new Map<string, EffectPlugin>()
const instrumentRegistry = new Map<string, InstrumentPlugin>()

export function registerEffect(plugin: EffectPlugin): void {
  effectRegistry.set(plugin.id, plugin)
}

export function registerInstrument(plugin: InstrumentPlugin): void {
  instrumentRegistry.set(plugin.id, plugin)
}

export function getEffect(id: string): EffectPlugin | undefined {
  return effectRegistry.get(id)
}

export function getInstrument(id: string): InstrumentPlugin | undefined {
  return instrumentRegistry.get(id)
}

export function listEffects(): EffectPlugin[] {
  return Array.from(effectRegistry.values())
}

export function listInstruments(): InstrumentPlugin[] {
  return Array.from(instrumentRegistry.values())
}

/** Group effect plugins by category — drives the DAW-style picker
 *  that shows "EQ > Dynamics > Modulation > ..." sections. */
export function listEffectsByCategory(): Record<
  EffectPlugin['category'],
  EffectPlugin[]
> {
  const groups: Record<EffectPlugin['category'], EffectPlugin[]> = {
    eq: [],
    dynamics: [],
    modulation: [],
    time: [],
    distortion: [],
    utility: [],
  }
  for (const plugin of effectRegistry.values()) {
    groups[plugin.category].push(plugin)
  }
  return groups
}

export function listInstrumentsByCategory(): Record<
  InstrumentPlugin['category'],
  InstrumentPlugin[]
> {
  const groups: Record<InstrumentPlugin['category'], InstrumentPlugin[]> = {
    sampler: [],
    synth: [],
    hybrid: [],
    external: [],
  }
  for (const plugin of instrumentRegistry.values()) {
    groups[plugin.category].push(plugin)
  }
  return groups
}
